import { useEffect, useState, type CSSProperties } from 'react';
import { ApiError } from '../api/client';
import { getCrmStats, type CrmStats as Stats } from '../api/crm';
import { Button } from '../components/arc/button/button';
import { ArcPanel, ArcSelect, ArcSummaryGrid, ArcSummaryMetric } from '../components/arc/primitives/arc-primitives';
import { Progress } from '../components/arc/progress/progress';
import { SortableDataTable, type DataColumn } from '../components/arc/sortable-data-table/sortable-data-table';
import { stagesForSlug } from './crm-stages';
import { money, moneyAlt, moneyUnit, CZK_PER_EUR, czkFirst } from './crm-money';
import './crm-stats-arc.css';

const nf = new Intl.NumberFormat('cs-CZ');
type Period = '12' | '24' | 'all';
type MonthRow = Record<string, unknown> & {
  ym: string;
  total: number;
  won: number;
  value: number;
  wonValue: number;
};

function monthLabel(ym: string): string {
  const [year = 0, month = 1] = ym.split('-').map(Number);
  const label = new Date(year, month - 1, 1).toLocaleDateString('cs-CZ', { month: 'long', year: 'numeric' });
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function lastMonths(count: number): string[] {
  const result: string[] = [];
  const now = new Date();
  for (let index = 0; index < count; index++) {
    const date = new Date(now.getFullYear(), now.getMonth() - index, 1);
    result.push(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`);
  }
  return result;
}

export function CrmStats({ spaceSlug, onClose }: { spaceSlug: string | null; onClose: () => void }) {
  const [period, setPeriod] = useState<Period>('12');
  const [data, setData] = useState<Stats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const stages = stagesForSlug(spaceSlug);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    getCrmStats(period === 'all' ? 'all' : Number(period))
      .then((result) => { if (!cancelled) setData(result); })
      .catch((cause: unknown) => { if (!cancelled) setError(cause instanceof ApiError ? cause.message : 'Statistiky se nepodařilo načíst'); });
    return () => { cancelled = true; };
  }, [period]);

  const total = data?.total ?? 0;
  const won = data?.won ?? 0;
  const rate = total ? Math.round((won * 100) / total) : 0;
  const tableValue = data?.tableValue ?? data?.pipelineValue ?? 0;
  const closedValue = data?.closedValue ?? data?.wonValue ?? 0;
  const multiplier = czkFirst(spaceSlug) ? CZK_PER_EUR : 1;
  const monthKeys = data ? period === 'all' ? data.months.map((month) => month.ym) : lastMonths(Number(period)) : [];
  const monthRows: MonthRow[] = data ? monthKeys.map((ym) => {
    const month = data.months.find((item) => item.ym === ym);
    return { ym, total: month?.total ?? 0, won: month?.won ?? 0, value: month?.value ?? 0, wonValue: month?.wonValue ?? 0 };
  }) : [];

  const columns: DataColumn<MonthRow>[] = [
    { key: 'ym', label: 'Měsíc', width: '24%', render: (_, row) => <strong className="crm-stats-arc-month">{monthLabel(row.ym)}</strong> },
    { key: 'total', label: 'Leady', numeric: true, width: '15%', render: (_, row) => nf.format(row.total) },
    { key: 'won', label: 'Získané', numeric: true, width: '17%', render: (_, row) => <span className="crm-stats-arc-cell"><strong>{nf.format(row.won)}</strong><small>{row.total ? `${Math.round((row.won * 100) / row.total)} %` : '—'}</small></span> },
    { key: 'value', label: 'Hodnota / měsíc', numeric: true, width: '22%', render: (_, row) => <span className="crm-stats-arc-cell"><strong>{row.value ? money(row.value, spaceSlug) : '—'}</strong>{row.value && moneyAlt(row.value, spaceSlug) ? <small>{moneyAlt(row.value, spaceSlug)}</small> : null}</span> },
    { key: 'wonValue', label: 'Získaná hodnota', numeric: true, width: '22%', render: (_, row) => <span className="crm-stats-arc-cell"><strong>{row.wonValue ? money(row.wonValue, spaceSlug) : '—'}</strong>{row.wonValue && moneyAlt(row.wonValue, spaceSlug) ? <small>{moneyAlt(row.wonValue, spaceSlug)}</small> : null}</span> },
  ];

  return (
    <div className="crm-stats-arc">
      <div className="crm-stats-arc-toolbar">
        <Button type="button" variant="secondary" size="sm" onClick={onClose}>Zpět na leady</Button>
        <ArcSelect
          label="Období"
          value={period}
          options={[{ value: '12', label: 'Posledních 12 měsíců' }, { value: '24', label: 'Posledních 24 měsíců' }, { value: 'all', label: 'Celá historie' }]}
          onChange={(value) => setPeriod(value as Period)}
        />
      </div>

      {error ? <ArcPanel className="crm-stats-arc-error" role="alert">{error}</ArcPanel> : null}
      {!data && !error ? <div className="prototype-page-loading" aria-label="Načítám"><span /><span /><span /><span /></div> : null}

      {data ? <>
        <ArcSummaryGrid className="crm-stats-arc-summary">
          <ArcSummaryMetric label="Nové příležitosti" value={total} context="leadů přijatých v období" density="compact" />
          <ArcSummaryMetric label="Získané příležitosti" value={won} context={`${rate} % všech leadů`} density="compact" />
          <ArcSummaryMetric label="Na stole / měsíc" value={Math.round(tableValue * multiplier)} suffix={` ${moneyUnit(spaceSlug)}`} context="předpoklad příjmů" density="compact" />
          <ArcSummaryMetric label="Potvrzené / měsíc" value={Math.round(closedValue * multiplier)} suffix={` ${moneyUnit(spaceSlug)}`} context={`≈ ${money(closedValue * 12, spaceSlug)} ročně`} density="compact" />
        </ArcSummaryGrid>

        <ArcPanel className="crm-stats-arc-pipeline" aria-label="Leady podle fáze">
          <div className="crm-stats-arc-section-head">
            <div><span>Pipeline</span><h2>Leady podle fáze</h2></div>
            <small>{nf.format(total)} celkem</small>
          </div>
          <div className="crm-stats-arc-stages">
            {stages.map((stage) => {
              const count = data.byStatus[stage.key] ?? 0;
              const share = total ? Math.round((count * 1000) / total) / 10 : 0;
              return <div key={stage.key} className="crm-stats-arc-stage" style={{ '--accent': stage.color } as CSSProperties}>
                <div className="crm-stats-arc-stage-top"><span id={`crm-stats-stage-${stage.key}`}><i aria-hidden="true" />{stage.label}</span><strong>{nf.format(count)}</strong></div>
                <Progress value={count} max={total || 1} aria-labelledby={`crm-stats-stage-${stage.key}`} />
                <small>{share.toLocaleString('cs-CZ', { maximumFractionDigits: 1 })} % všech leadů</small>
              </div>;
            })}
          </div>
        </ArcPanel>

        <section className="crm-stats-arc-months" aria-label="Měsíční vývoj leadů">
          <div className="crm-stats-arc-section-head">
            <div><span>Vývoj</span><h2>Podle měsíce</h2><p>Leady a hodnota podle měsíce přijetí.</p></div>
            <small>{period === 'all' ? 'Celá historie' : `${period} měsíců`}</small>
          </div>
          <SortableDataTable rows={monthRows} columns={columns} rowKey="ym" caption="Měsíční vývoj leadů" emptyMessage="Zatím žádné leady." defaultSort={{ key: 'ym', direction: 'desc' }} />
        </section>
      </> : null}
    </div>
  );
}
